export type UserMessageAction = 'retry' | 'refresh' | 'none';

export interface UserMessage {
  title: string;
  body: string;
  contact?: string;
  action?: UserMessageAction;
}

/** Solo UX: qué hacer en el front. El texto lo manda el back en userMessage. */
export const ORDER_ERROR_ACTIONS: Record<string, UserMessageAction> = {
  ORDER_NOT_FOUND: 'refresh',
  ORDER_ALREADY_DELETED: 'refresh',
  ORDER_NOT_EDITABLE: 'refresh',
  PDF_RETRY_NOT_ALLOWED: 'refresh',
  PDF_GENERATION_FAILED: 'retry',
  CREATE_ORDER_JOB_NOT_FOUND: 'retry',
  CREATE_ORDER_JOB_ID_REQUIRED: 'retry',
  CREATE_ORDER_JOB_FAILED: 'retry',
  TIMEOUT_CREATE_ORDER_JOB: 'retry',
};

const TIMEOUT_CREATE_ORDER_JOB_MESSAGE: UserMessage = {
  title: 'Tiempo de espera agotado',
  body: 'La creación de órdenes está tardando más de lo esperado. Reintenta o contacta a Soporte TI.',
  action: 'retry',
};

const DEFAULT_FALLBACK =
  'Ocurrió un error inesperado. Contacta a Soporte TI.';

export function getOrderErrorAction(
  code: string | null | undefined
): UserMessageAction {
  const normalized = code?.trim().toUpperCase();
  if (!normalized) {
    return 'none';
  }
  return ORDER_ERROR_ACTIONS[normalized] ?? 'none';
}

export function resolveUserMessage(
  code: string | null | undefined,
  maps: Record<string, UserMessage>[] = [],
  fallback = DEFAULT_FALLBACK,
  backendUserMessage?: string | null
): UserMessage {
  const normalized = code?.trim().toUpperCase();
  const fromBackend = backendUserMessage?.trim();
  const catalog = lookupCatalogMessage(code, maps);
  const action = getOrderErrorAction(code);

  if (normalized === 'TIMEOUT_CREATE_ORDER_JOB' && !fromBackend) {
    return TIMEOUT_CREATE_ORDER_JOB_MESSAGE;
  }

  if (fromBackend) {
    return {
      title: catalog?.title ?? '',
      body: fromBackend,
      action: catalog?.action ?? action,
    };
  }

  if (catalog) {
    return catalog;
  }

  return { title: 'Error', body: fallback, action };
}

export function resolveCreateOrderRejectionMessage(rejected: {
  reason?: string | null;
  userMessage?: string | null;
}): UserMessage {
  return resolveUserMessage(
    rejected?.reason,
    [],
    'No se pudo generar la orden para este servicio.',
    rejected?.userMessage
  );
}

export function resolveCreateOrderWarningMessage(warning: {
  reason?: string | null;
  userMessage?: string | null;
}): UserMessage {
  return resolveUserMessage(
    warning?.reason,
    [],
    'Advertencia.',
    warning?.userMessage
  );
}

export function formatUserMessageText(message: UserMessage): string {
  return message.contact ? `${message.body} ${message.contact}` : message.body;
}

function lookupCatalogMessage(
  code: string | null | undefined,
  maps: Record<string, UserMessage>[]
): UserMessage | null {
  const normalized = code?.trim().toUpperCase();
  if (!normalized) {
    return null;
  }

  for (const map of maps) {
    if (map[normalized]) {
      return map[normalized];
    }
  }

  return null;
}
